import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  FolderIcon,
  FolderOpenIcon,
  DocumentIcon,
  EyeIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  SignalIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const BrowserContainer = styled.div`
  background: ${(p) => (p.$dark ? '#0f172a' : theme.colors.background.paper)};
  border: 1px solid ${(p) => (p.$dark ? '#475569' : theme.colors.border.primary)};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${(p) =>
    p.$pickerMode
      ? `
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
  `
      : `
    max-height: 520px;
  `}
`;

const BrowserHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BrowserTitle = styled.h3`
  color: ${(p) => (p.$dark ? '#f8fafc' : theme.colors.text.primary)};
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
  
  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: ${theme.colors.text.secondary};
`;

const DataTree = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  ${(p) => (p.$pickerMode ? '' : 'max-height: 400px;')}
  
  /* Prevent scroll events from bubbling up */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.background.primary};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border.secondary};
    border-radius: 4px;
    
    &:hover {
      background: ${theme.colors.text.secondary};
    }
  }
`;

const TreeNode = styled.div`
  border-bottom: 1px solid ${theme.colors.border.secondary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${theme.colors.background.primary};
  }
`;

const NodeIcon = styled.div`
  margin-right: 0.5rem;
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const NodeLabel = styled.div`
  flex: 1;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
`;

const NodeValue = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.xs};
  font-family: ${theme.typography.fontFamily.mono};
`;

const NodeActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.25rem;
  background: transparent;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
  
  &:hover {
    background: ${theme.colors.primary.main};
    color: white;
    border-color: ${theme.colors.primary.main};
  }
`;

const NodeChildren = styled.div`
  padding-left: 1.5rem;
  background: ${theme.colors.background.primary};
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: ${theme.colors.background.secondary};
  border-top: 1px solid ${theme.colors.border.secondary};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
`;

const SelectPathButton = styled.button`
  padding: 0.25rem 0.55rem;
  background: ${theme.colors.primary.main};
  border: 1px solid ${theme.colors.primary.main};
  border-radius: ${theme.borderRadius.sm};
  color: #fff;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.primary.dark};
  }
`;

const PickerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(59, 130, 246, 0.12);
  border-top: 1px solid ${theme.colors.border.secondary};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
`;

const DataBrowser = ({
  vesselId = 1,
  onPathSelect,
  selectedPaths = [],
  dataSource = 'database',
  connectionId = null,
  connectionName = null,
  pickerMode = false,
  explorerHref = null,
  onShowCatalogTab = null,
  dark = false,
}) => {
  const [signalKData, setSignalKData] = useState({});
  const [expandedNodes, setExpandedNodes] = useState(new Set(['electrical', 'navigation', 'tanks']));
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [totalPaths, setTotalPaths] = useState(0);
  const [pathCounts, setPathCounts] = useState({ live: 0, stale: 0, total: 0 });

  useEffect(() => {
    loadSignalKData();
    const interval = setInterval(loadSignalKData, 30000);
    return () => clearInterval(interval);
  }, [vesselId, dataSource, statusFilter]);

  const catalogRowsToTree = (rows) => {
    const out = {};
    const sorted = [...rows].sort(
      (a, b) => b.path.split('.').length - a.path.split('.').length
    );

    const ensureBranch = (root, parts) => {
      let cur = root;
      for (let i = 0; i < parts.length; i++) {
        const key = parts[i];
        if (!cur[key]) {
          cur[key] = {};
        } else if (isLeafNode(cur[key])) {
          const existing = cur[key];
          cur[key] = { _self: existing };
        }
        cur = cur[key];
      }
      return cur;
    };

    for (const row of sorted) {
      const parts = row.path.split('.');
      if (parts.length < 2) continue;
      const parentParts = parts.slice(0, -1);
      const leafKey = parts[parts.length - 1];
      const parent = ensureBranch(out, parentParts);
      parent[leafKey] = {
        value: row.value,
        unit: row.units || '',
        timestamp: row.lastSeen,
        $source: row.source,
        status: row.status,
      };
    }
    return out;
  };

  const isLeafNode = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
    if (Object.keys(data).length === 1 && data._self) return true;
    return Object.prototype.hasOwnProperty.call(data, 'value');
  };

  const countPaths = (obj, prefix = '') => {
    let count = 0;
    if (!obj || typeof obj !== 'object') return 0;
    if (obj._self && prefix) count += 1;
    for (const key in obj) {
      if (key === '_self' || key === '$source') continue;
      const path = prefix ? `${prefix}.${key}` : key;
      const child = obj[key];
      if (child && typeof child === 'object' && !isLeafNode(child)) {
        count += countPaths(child, path);
      } else if (isLeafNode(child)) {
        count += 1;
      }
    }
    return count;
  };

  const loadSignalKData = async () => {
    setLoadError(null);
    try {
      const vid = parseInt(vesselId, 10);
      let data = {};

      if (dataSource === 'demo') {
        const response = await api.get('/api/demo/snapshot');
        data = response.data || {};
      } else if (vid) {
        try {
          const catalogRes = await api.get(
            `/api/vessels/${vid}/signalk/catalog?status=${statusFilter}&limit=5000`
          );
          const rows = catalogRes.data.paths || [];
          if (rows.length > 0) {
            data = catalogRowsToTree(rows);
            setPathCounts(catalogRes.data.counts || { live: 0, stale: 0, total: 0 });
          } else {
            const response = await api.get(`/api/vessels/${vid}/signalk/latest`);
            data = response.data || {};
            setPathCounts({ live: countPaths(data), stale: 0, total: countPaths(data) });
          }
        } catch (catalogErr) {
          console.warn('Catalog load failed, falling back to latest:', catalogErr.message);
          const response = await api.get(`/api/vessels/${vid}/signalk/latest`);
          data = response.data || {};
          setPathCounts({ live: countPaths(data), stale: 0, total: countPaths(data) });
        }
      } else {
        const response = await api.get('/api/signalk/data');
        data = response.data || {};
      }

      if (!data || Object.keys(data).length === 0) {
        setLoadError('No SignalK data found for this vessel. Check the plugin stream is connected.');
      }

      setSignalKData(data);
      setTotalPaths(countPaths(data));
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error(`Error loading ${dataSource} SignalK data:`, error);
      const msg = error.response?.data?.error || error.message || 'Unknown error';
      setLoadError(msg);
      toast.error(`Failed to load SignalK data: ${msg}`);
      setSignalKData({});
      setLoading(false);
    }
  };

  const toggleNode = (path) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedNodes(newExpanded);
  };

  const handleSelectPath = (path, data) => {
    if (onPathSelect) {
      onPathSelect(path, data);
    }
    if (pickerMode) {
      toast.success(`Path selected — click Save Widget to add it`);
    } else {
      toast.success(`Selected: ${path}`);
    }
  };

  const handleNodeActivate = (fullPath, data, hasChildren) => {
    if (hasChildren) {
      toggleNode(fullPath);
      return;
    }
    handleSelectPath(fullPath, data);
  };

  const handlePreviewPath = (path, data) => {
    toast.success(`${path}: ${data.value} ${data.unit || ''}`);
  };

  const filterData = (obj, prefix = '') => {
    if (!searchTerm) return obj;
    
    const filtered = {};
    for (const key in obj) {
      const path = prefix ? `${prefix}.${key}` : key;
      
      if (path.toLowerCase().includes(searchTerm.toLowerCase())) {
        filtered[key] = obj[key];
      } else if (obj[key] && typeof obj[key] === 'object' && !isLeafNode(obj[key])) {
        const childFiltered = filterData(obj[key], path);
        if (Object.keys(childFiltered).length > 0) {
          filtered[key] = childFiltered;
        }
      }
    }
    return filtered;
  };

  const renderNode = (key, data, path = '', level = 0) => {
    const fullPath = path ? `${path}.${key}` : key;
    const isExpanded = expandedNodes.has(fullPath);
    const hasChildren = !!(data && typeof data === 'object' && !Array.isArray(data) && !isLeafNode(data));
    const isSelected = selectedPaths.includes(fullPath);
    const displayKey = key === '_self' ? path.split('.').pop() || key : key;

    return (
      <TreeNode key={fullPath}>
        <NodeHeader
          onClick={() => handleNodeActivate(fullPath, data, hasChildren)}
          style={{
            background: isSelected ? 'rgba(59, 130, 246, 0.12)' : undefined,
            cursor: 'pointer',
          }}
        >
          <NodeIcon>
            {hasChildren ? (
              isExpanded ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />
            ) : (
              <DocumentIcon className="w-4 h-4" />
            )}
          </NodeIcon>
          
          <NodeIcon>
            {hasChildren ? (
              isExpanded ? <FolderOpenIcon className="w-4 h-4" /> : <FolderIcon className="w-4 h-4" />
            ) : (
              <SignalIcon className="w-4 h-4" />
            )}
          </NodeIcon>
          
          <NodeLabel style={{ fontWeight: isSelected ? theme.fontWeights.bold : 'normal' }}>
            {displayKey}
          </NodeLabel>
          
          {!hasChildren && data && (
            <>
              <NodeValue>
                {(() => {
                  const leaf = data._self || data;
                  const v = leaf.value;
                  if (v === null || v === undefined) return '';
                  if (typeof v === 'object') {
                    if ('raw' in v && v.raw !== undefined && v.raw !== null) return String(v.raw);
                    try { return JSON.stringify(v); } catch { return String(v); }
                  }
                  return String(v);
                })()} {(data._self || data).unit || ''}
              </NodeValue>
              <NodeActions>
                {pickerMode ? (
                  <SelectPathButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectPath(fullPath, data._self || data);
                    }}
                  >
                    Use path
                  </SelectPathButton>
                ) : (
                  <ActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewPath(fullPath, data._self || data);
                    }}
                    title="Preview value"
                    type="button"
                  >
                    <EyeIcon />
                  </ActionButton>
                )}
              </NodeActions>
            </>
          )}
        </NodeHeader>
        
        {hasChildren && isExpanded && (
          <NodeChildren>
            {data._self ? (
              <TreeNode key={`${fullPath}::__value`}>
                <NodeHeader
                  onClick={() => handleSelectPath(fullPath, data._self)}
                  style={{
                    background: selectedPaths.includes(fullPath) ? 'rgba(59, 130, 246, 0.12)' : undefined,
                    cursor: 'pointer',
                  }}
                >
                  <NodeIcon><DocumentIcon className="w-4 h-4" /></NodeIcon>
                  <NodeIcon><SignalIcon className="w-4 h-4" /></NodeIcon>
                  <NodeLabel style={{ fontStyle: 'italic' }}>value</NodeLabel>
                  <NodeValue>{String(data._self.value ?? '')} {data._self.unit || ''}</NodeValue>
                  <NodeActions>
                    {pickerMode ? (
                      <SelectPathButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectPath(fullPath, data._self);
                        }}
                      >
                        Use path
                      </SelectPathButton>
                    ) : null}
                  </NodeActions>
                </NodeHeader>
              </TreeNode>
            ) : null}
            {Object.keys(data)
              .filter((childKey) => childKey !== '_self')
              .sort()
              .map((childKey) => {
                const child = data[childKey];
                return renderNode(String(childKey), child, fullPath, level + 1);
              })}
          </NodeChildren>
        )}
      </TreeNode>
    );
  };

  const filteredData = filterData(signalKData);

  if (loading) {
    return (
      <BrowserContainer $dark={dark} $pickerMode={pickerMode}>
        <BrowserHeader>
          <BrowserTitle>
            <SignalIcon className="w-5 h-5" />
            {connectionName ? `${connectionName} - SignalK Data` : 'SignalK Data Browser'}
          </BrowserTitle>
        </BrowserHeader>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          flex: 1,
          color: theme.colors.text.secondary 
        }}>
          Loading SignalK data...
        </div>
      </BrowserContainer>
    );
  }

  return (
    <BrowserContainer $dark={dark} $pickerMode={pickerMode}>
      <BrowserHeader>
        <BrowserTitle>
          <SignalIcon className="w-5 h-5" />
          SignalK Data Browser - {dataSource.charAt(0).toUpperCase() + dataSource.slice(1)}
        </BrowserTitle>
      </BrowserHeader>

      <SearchContainer>
        <SearchIcon />
        <SearchInput
          type="text"
          placeholder="Search SignalK paths..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      {dataSource !== 'demo' && vesselId && !pickerMode ? (
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.4rem 0.6rem',
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.border.secondary}`,
              background: theme.colors.background.secondary,
              color: theme.colors.text.primary,
              fontSize: theme.fontSizes.sm,
            }}
          >
            <option value="all">All paths (ever seen)</option>
            <option value="live">Live only</option>
            <option value="stale">Stale</option>
            <option value="archived">Archived</option>
          </select>
          <Link
            to={`/vessels/${vesselId}/live`}
            style={{ fontSize: theme.fontSizes.sm, color: theme.colors.primary.main }}
          >
            Live Boat overview →
          </Link>
          <Link
            to={explorerHref || `/vessels/${vesselId}/signalk/explore`}
            style={{ fontSize: theme.fontSizes.sm, color: theme.colors.primary.main }}
          >
            Open SignalK Explorer →
          </Link>
        </div>
      ) : null}

      {pickerMode ? (
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.65rem 0.75rem',
          borderRadius: theme.borderRadius.md,
          background: dark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
          border: `1px solid ${dark ? '#475569' : theme.colors.border.secondary}`,
          fontSize: theme.fontSizes.sm,
          color: dark ? '#cbd5e1' : theme.colors.text.secondary,
        }}>
          Expand a branch, then click a <strong style={{ color: dark ? '#f8fafc' : theme.colors.text.primary }}>value row</strong> or
          {' '}<strong style={{ color: dark ? '#f8fafc' : theme.colors.text.primary }}>Use path</strong>.
          {onShowCatalogTab ? (
            <>
              {' '}Prefer search? Use the <button type="button" onClick={onShowCatalogTab} style={{ background: 'none', border: 'none', color: theme.colors.primary.main, cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>Path catalog</button> tab.
            </>
          ) : null}
        </div>
      ) : null}

      <DataTree
        $pickerMode={pickerMode}
        onWheel={(e) => {
          // Prevent scroll events from bubbling up to parent containers
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          // Prevent touch scroll events from bubbling up
          e.stopPropagation();
        }}
      >
        {Object.keys(filteredData).length === 0 ? (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            color: theme.colors.text.secondary 
          }}>
            {loadError
              ? loadError
              : searchTerm
                ? 'No matching paths found'
                : 'No SignalK data available'}
          </div>
        ) : (
          Object.keys(filteredData)
            .sort()
            .map(key => renderNode(key, filteredData[key]))
        )}
      </DataTree>

      <StatusBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ClockIcon style={{ width: '0.75rem', height: '0.75rem' }} />
          Last updated: {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}
        </div>
        <div>
          Showing {totalPaths.toLocaleString()} paths
          {pathCounts.total > 0
            ? ` (${pathCounts.live || 0} live, ${pathCounts.stale || 0} stale, ${pathCounts.total} catalog)`
            : ''}
          {pickerMode
            ? ' — pick a leaf reading, then Save Widget'
            : ' — click a value row to select'}
        </div>
      </StatusBar>

      {pickerMode && selectedPaths.length > 0 ? (
        <PickerFooter>
          <span style={{ fontFamily: theme.typography?.fontFamily?.mono || 'monospace', fontSize: theme.fontSizes.xs }}>
            {selectedPaths[0]}
          </span>
        </PickerFooter>
      ) : null}
    </BrowserContainer>
  );
};

export default DataBrowser;